// SelectorVistaDialog — elegir con qué rol y en qué entidad se quiere
// trabajar. Ver la explicación de "vista activa" en context/AuthContext.
//
//   • Al iniciar sesión con VARIAS vistas posibles: aparece solo y no se
//     puede cerrar sin elegir.
//   • Después: se abre desde el menú del avatar ("Cambiar de vista") y sí
//     se puede cancelar.
// Cambiar de vista solo cambia lo que se muestra; los permisos siguen
// siendo los roles reales del usuario.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  AccountBalance as FederacionIcon,
  Apartment as CooperativaIcon,
  Person as AfiliadoIcon,
  Public as ConfederacionIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const ICONO_POR_NIVEL = {
  confederacion: ConfederacionIcon,
  federacion: FederacionIcon,
  cooperativa: CooperativaIcon,
  afiliado: AfiliadoIcon,
};

export default function SelectorVistaDialog() {
  const { vistas, vistaActivaId, necesitaSeleccion, selectorAbierto, setSelectorAbierto, seleccionarVista, getNivelLabel, getNivelColor } =
    useAuth();
  const navigate = useNavigate();
  const [elegida, setElegida] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const abierto = necesitaSeleccion || selectorAbierto;

  // Al abrir, preselecciona la vista actual (o la primera).
  useEffect(() => {
    if (abierto) setElegida(vistaActivaId || vistas[0]?.id || null);
  }, [abierto, vistaActivaId, vistas]);

  const continuar = async () => {
    if (!elegida) return;
    setEnviando(true);
    try {
      await seleccionarVista(elegida);
      navigate('/dashboard'); // el menú cambia con la vista: se vuelve al inicio
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog
      open={abierto}
      onClose={necesitaSeleccion ? undefined : () => setSelectorAbierto(false)}
      disableEscapeKeyDown={necesitaSeleccion}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{necesitaSeleccion ? '¿Cómo quieres entrar?' : 'Cambiar de vista'}</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tienes más de un rol en CoopNet. Elige con cuál y en qué entidad quieres trabajar. Puedes cambiarlo cuando quieras
          desde el menú de tu avatar (arriba a la derecha).
        </Typography>

        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {vistas.map((v) => {
            const Icono = ICONO_POR_NIVEL[v.nivel] || CooperativaIcon;
            const color = getNivelColor(v.nivel);
            return (
              <ListItemButton
                key={v.id}
                selected={elegida === v.id}
                onClick={() => setElegida(v.id)}
                sx={{ borderRadius: 2, border: '1px solid', borderColor: elegida === v.id ? color : 'divider' }}
              >
                <ListItemIcon sx={{ minWidth: 44, color }}>
                  <Icono />
                </ListItemIcon>
                <ListItemText primary={v.titulo} secondary={v.detalle} primaryTypographyProps={{ fontWeight: 600 }} />
                <Box sx={{ ml: 1 }}>
                  <Chip size="small" label={getNivelLabel(v.nivel)} sx={{ color, borderColor: color }} variant="outlined" />
                </Box>
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!necesitaSeleccion && <Button onClick={() => setSelectorAbierto(false)}>Cancelar</Button>}
        <Button
          variant="contained"
          onClick={continuar}
          disabled={!elegida || enviando}
          startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : null}
        >
          Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
